/**
 * AdminBackendMisconfigAlert — Iter-221
 *
 * Inline alert that ONLY renders for authenticated admins. Replaces the
 * public-facing red banner that used to be injected by api.js into the
 * DOM for every visitor (including prospects on the login page) — that
 * banner was visible to anyone, made the platform look broken, and was
 * removed in Iter-221.
 *
 * Diagnostic source: `window.__IEVNTUR_BACKEND_MISCONFIG__` is set by the
 * boot safety check in utils/api.js whenever the bundle was built with an
 * unsafe REACT_APP_BACKEND_URL while served from app.ievntur.com.
 *
 * Mount this component on admin home / admin dashboard only — never on
 * unauthenticated routes.
 */
import React from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';

const AdminBackendMisconfigAlert = () => {
  const info = typeof window !== 'undefined' ? window.__IEVNTUR_BACKEND_MISCONFIG__ : null;
  if (!info) return null;

  return (
    <div
      data-testid="admin-backend-misconfig-alert"
      className="rounded-lg border border-rose-300 bg-rose-50 p-4 mb-4"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-rose-900">
            Production backend misconfiguration detected
          </p>
          <p className="text-xs text-rose-800 mt-1 leading-relaxed">
            This bundle was built with{' '}
            <code className="px-1 py-0.5 bg-rose-100 rounded">
              REACT_APP_BACKEND_URL={info.backend || '(empty)'}
            </code>{' '}
            but is being served from{' '}
            <code className="px-1 py-0.5 bg-rose-100 rounded">{info.host}</code>.
            Mobile carriers (Verizon ActiveArmor, AT&amp;T, T-Mobile) block{' '}
            <code className="px-1 py-0.5 bg-rose-100 rounded">*.emergent.host</code>,
            so API calls from those networks may fail.
          </p>
          <p className="text-xs text-rose-700 mt-2">
            <strong>Fix:</strong> Update the production environment variable to{' '}
            <code className="px-1 py-0.5 bg-rose-100 rounded">https://app.ievntur.com</code>,
            then trigger a fresh build &amp; redeploy. This alert is admin-only — public
            visitors don&apos;t see it.
          </p>
          <a
            href="https://emergent.sh/support"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-rose-900 underline mt-2"
            data-testid="admin-backend-misconfig-support-link"
          >
            Contact Emergent Support <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminBackendMisconfigAlert;
